import { DASH_META_ROOT, getAuthDashMetaCsrfToken } from '@revelio/auth';

import { consumeSse } from './global-ai-run-page-stream';
import { turnStepEventSchema } from './global-ai-turn-step-event.schema';
import type {
  TurnStepEvent,
  TurnStepRequestBody,
} from './global-ai-turn-step-stream';

/** One progress checkpoint POSTed to the narrate-progress endpoint. */
export type NarrateProgressRequestBody = {
  userGoal: string;
  recentNarration: string[];
  event: { kind: string; digest: unknown };
  context?: TurnStepRequestBody['context'];
};

/**
 * POST one progress checkpoint and stream back a grounded sentence, reporting
 * cumulative text on each `text` event. Throws on a non-OK response; the caller
 * swallows failures since narration is best-effort.
 */
export async function runNarrateProgressStep(params: {
  body: NarrateProgressRequestBody;
  onText: (cumulativeText: string) => void;
  signal?: AbortSignal;
}): Promise<void> {
  const csrfToken = getAuthDashMetaCsrfToken();
  const response = await fetch(
    `${DASH_META_ROOT}/api/build-a-dash/ai-gen/global-assistant/narrate-progress`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'x-surf-token-dash-meta': csrfToken ?? '',
      },
      body: JSON.stringify(params.body),
      signal: params.signal,
    }
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `HTTP ${response.status}`);
  }
  let full = '';
  await consumeSse(
    response,
    (line) => {
      if (!line.startsWith('data: ')) return;
      const raw = line.slice('data: '.length);
      let parsed: unknown;
      try {
        parsed = JSON.parse(raw);
      } catch {
        return; // ignore malformed progress chunks — best-effort
      }
      const result = turnStepEventSchema.safeParse(parsed);
      if (!result.success) return;
      const event = result.data as TurnStepEvent;
      if (event.type === 'text') {
        full += event.content;
        params.onText(full);
      }
    },
    params.signal
  );
}
